import { useCallback, useEffect, useRef, useState } from 'react';
import { correctTranscription } from '../../utils/transcriptionUtils';

/**
 * Custom hook for Web Speech API speech recognition
 * Handles continuous speech recognition with auto-restart and error recovery
 *
 * @param {Object} options
 * @param {Object} options.wordCorrections - Custom word corrections map
 * @param {string} options.language - Recognition language (default: 'en-US')
 * @param {Function} options.onTranscriptUpdate - Callback when transcript updates
 * @param {Function} options.onError - Error callback
 * @param {Function} options.onStatusChange - Status change callback
 * @returns {Object} Speech recognition controls and state
 */
const useSpeechRecognition = (options = {}) => {
  const {
    wordCorrections = {},
    language = 'en-US',
    onTranscriptUpdate,
    onError,
    onStatusChange,
  } = options;

  // State
  const [transcript, setTranscript] = useState('');
  const [isSupported, setIsSupported] = useState(true);
  const [status, setStatus] = useState('idle'); // idle, listening, no-speech, error

  // Refs
  const recognitionRef = useRef(null);
  const isRecordingRef = useRef(false);
  const sessionTranscriptRef = useRef('');
  const wordCorrectionsRef = useRef(wordCorrections);

  // Store callbacks in refs to avoid recreating recognition on every render
  const onErrorRef = useRef(onError);
  const onTranscriptUpdateRef = useRef(onTranscriptUpdate);
  const onStatusChangeRef = useRef(onStatusChange);

  // Keep refs in sync with latest callbacks
  useEffect(() => {
    wordCorrectionsRef.current = wordCorrections;
  }, [wordCorrections]);

  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);

  useEffect(() => {
    onTranscriptUpdateRef.current = onTranscriptUpdate;
  }, [onTranscriptUpdate]);

  useEffect(() => {
    onStatusChangeRef.current = onStatusChange;
  }, [onStatusChange]);

  // Update status and notify (stable function that reads from ref)
  const updateStatus = useCallback((newStatus) => {
    setStatus(newStatus);
    onStatusChangeRef.current?.(newStatus);
  }, []);

  // Initialize recognition (only recreate when language changes)
  useEffect(() => {
    // Check the standard name as well as the webkit-prefixed one.
    const SpeechRecognitionCtor =
      window.SpeechRecognition || window.webkitSpeechRecognition;

    if (!SpeechRecognitionCtor) {
      setIsSupported(false);
      // Deliberately does NOT call onError: the consumer routes errors into a
      // banner, and an unsupported browser is not an error state — typing is a
      // fully working way to do the interview. Reporting it as an error meant
      // Firefox users were shown a failure for a session that would have
      // worked fine. `isSupported` drives an inline hint instead.
      return;
    }

    const speechRecognition = new SpeechRecognitionCtor();
    speechRecognition.continuous = true;
    speechRecognition.interimResults = true;
    speechRecognition.lang = language;
    speechRecognition.maxAlternatives = 1;

    // Set grammar if available
    if ('grammars' in speechRecognition) {
      const SpeechGrammarListCtor =
        window.SpeechGrammarList || window.webkitSpeechGrammarList;
      if (SpeechGrammarListCtor) {
        speechRecognition.grammars = new SpeechGrammarListCtor();
      }
    }

    // Helper to safely restart recognition
    const safeRestartRecognition = () => {
      try {
        speechRecognition.start();
        updateStatus('listening');
      } catch (err) {
        if (err.name !== 'InvalidStateError') {
          console.error('Failed to restart SpeechRecognition:', err);
        }
      }
    };

    // Track current utterance
    let currentUtterance = '';

    // Handle results
    speechRecognition.onresult = (event) => {
      currentUtterance = '';

      for (let i = 0; i < event.results.length; i++) {
        const resultTranscript = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          if (i >= event.resultIndex) {
            sessionTranscriptRef.current += resultTranscript + ' ';
          }
        } else {
          currentUtterance += resultTranscript + ' ';
        }
      }

      const currentText = (sessionTranscriptRef.current + currentUtterance).trim();
      const correctedText = correctTranscription(currentText, wordCorrectionsRef.current);

      setTranscript(correctedText);
      onTranscriptUpdateRef.current?.(correctedText);
    };

    // Handle recognition end
    speechRecognition.onend = () => {
      if (isRecordingRef.current) {
        currentUtterance = '';
        setTimeout(() => {
          if (isRecordingRef.current) {
            safeRestartRecognition();
          }
        }, 150);
      }
    };

    // Handle errors
    speechRecognition.onerror = (event) => {
      // `no-speech` fires on every quiet stretch — roughly every 9 seconds while
      // the candidate is thinking — and the handler below treats it as normal.
      // Logging it as an error buried the ones that actually matter.
      if (event.error !== 'no-speech') {
        console.error('Speech recognition error:', event.error);
      }

      switch (event.error) {
        case 'no-speech':
          updateStatus('no-speech');
          setTimeout(() => {
            if (isRecordingRef.current) {
              updateStatus('listening');
            }
          }, 2000);
          break;

        case 'audio-capture':
          updateStatus('error');
          onErrorRef.current?.('Microphone access error. Please check your microphone permissions.');
          break;

        case 'not-allowed':
          updateStatus('error');
          onErrorRef.current?.('Speech recognition not allowed. Please enable microphone permissions.');
          break;

        case 'network':
          updateStatus('error');
          if (isRecordingRef.current) {
            setTimeout(() => {
              if (isRecordingRef.current) {
                safeRestartRecognition();
              }
            }, 500);
          }
          break;

        case 'aborted':
          if (isRecordingRef.current) {
            setTimeout(() => {
              if (isRecordingRef.current) {
                safeRestartRecognition();
              }
            }, 150);
          }
          break;

        default:
          updateStatus('error');
          break;
      }
    };

    // Additional event handlers
    speechRecognition.onstart = () => {
      updateStatus('listening');
    };

    speechRecognition.onspeechstart = () => {
      updateStatus('listening');
    };

    speechRecognition.onspeechend = () => {
      // Speech ended, recognition will continue
    };

    speechRecognition.onnomatch = () => {
      // No match found
    };

    recognitionRef.current = speechRecognition;

    // Cleanup
    return () => {
      try {
        speechRecognition.stop();
      } catch (e) {
        // Ignore errors during cleanup
      }
    };
    // Only recreate recognition when language changes
    // Callbacks are stored in refs to avoid unnecessary recreation
  }, [language, updateStatus]);

  // Start recognition
  const start = useCallback(() => {
    if (!recognitionRef.current || !isSupported) {
      console.error('Speech recognition not available');
      return false;
    }

    try {
      // First, try to stop any existing recognition
      try {
        recognitionRef.current.stop();
      } catch (e) {
        // Ignore stop errors
      }

      // Small delay to ensure clean state
      isRecordingRef.current = true;
      sessionTranscriptRef.current = '';
      setTranscript('');

      // Start recognition
      recognitionRef.current.start();
      updateStatus('listening');
      return true;
    } catch (error) {
      // Handle various "already started" error messages across browsers
      const errorMsg = error.message?.toLowerCase() || '';
      if (
        errorMsg.includes('already started') ||
        errorMsg.includes('recognition has already started') ||
        error.name === 'InvalidStateError'
      ) {
        // Already running, just update state
        isRecordingRef.current = true;
        updateStatus('listening');
        return true;
      }
      console.error('Error starting speech recognition:', error);
      onErrorRef.current?.(error.message || 'Failed to start speech recognition');
      updateStatus('error');
      return false;
    }
  }, [isSupported, updateStatus]);

  // Stop recognition
  const stop = useCallback(() => {
    isRecordingRef.current = false;
    try {
      recognitionRef.current?.stop();
    } catch (e) {
      // Ignore errors when stopping
    }
    updateStatus('idle');
  }, [updateStatus]);

  // Reset transcript
  const reset = useCallback(() => {
    sessionTranscriptRef.current = '';
    setTranscript('');
  }, []);

  // Get final transcript (for use after stopping)
  const getFinalTranscript = useCallback(() => {
    return transcript;
  }, [transcript]);

  return {
    // State
    transcript,
    status,
    isSupported,

    // Controls
    start,
    stop,
    reset,
    getFinalTranscript,

    // Refs for external access if needed
    isRecordingRef,
    sessionTranscriptRef,
  };
};

export default useSpeechRecognition;
